import {
  alignChannelFromBackend,
  isExportChannel,
  isExternalChannel,
  isIntegrationChannel,
} from 'src/data/channel';
import { parseJson } from 'src/lib/common';

import { LOADING_TALKING_POINTS_TEXT } from './const';
import { isDisconnectedStatus, PERFORMANCE_EXTRACTION_STATUS } from './extraction';
import { isEmailCsvResourceDocument } from './resources';

export const flatConnectedAccounts = (connectedAccounts, withResources) =>
  withResources
    ? Object.values(connectedAccounts || {}).reduce((acc, accounts) => {
        return acc.concat(accounts);
      }, [])
    : Object.keys(connectedAccounts || {}).reduce((acc, channel) => {
        if (
          isIntegrationChannel(channel) ||
          isExternalChannel(channel) ||
          isExportChannel(channel)
        ) {
          return acc.concat(connectedAccounts[channel]);
        }
        return acc;
      }, []);

export const getAssociatedAccountIds = (workspace) => {
  const { associatedIntegrationIds } = workspace.associatedAccounts.reduce(
    (acc, account) => {
      if (
        (isIntegrationChannel(alignChannelFromBackend(account.accountType)) ||
          isExternalChannel(alignChannelFromBackend(account.accountType)) ||
          isExportChannel(alignChannelFromBackend(account.accountType)) ||
          isEmailCsvResourceDocument(account.accountType)) &&
        !isDisconnectedStatus(account.status)
      ) {
        acc.associatedIntegrationIds.push(account.accountId);
      }
      return acc;
    },
    { associatedIntegrationIds: [] }
  );

  return { associatedIntegrationIds };
};

export const convertAccountsListToChannelMap = (accounts = []) => {
  const result = {};

  accounts.forEach((account) => {
    const channel = alignChannelFromBackend(account.accountType || account.dataSource);

    const channelAccounts = result[channel] || [];

    const preparedAccount = {
      ...account,
      name: account.accountName,
      id: account.accountId,
      integrationAssetId: account.id,
      channel,
    };

    result[channel] = [...channelAccounts, preparedAccount];
  });

  return result;
};

export const getPerformanceLoaderLabel = ({
  channel,
  progressPercent,
  isTalkingPointsView,
  isTrainingModel,
  isAlreadyConnected,
}) => {
  if (isTalkingPointsView && isAlreadyConnected) {
    return LOADING_TALKING_POINTS_TEXT;
  }
  if (isTrainingModel && isAlreadyConnected) {
    return 'Training model...';
  }

  if (progressPercent < 25) {
    return isEmailCsvResourceDocument(channel) ? 'Uploading...' : `Downloading data...`;
  }
  if (progressPercent < 50) {
    return 'Defining default performance benchmark sets...';
  }
  if (progressPercent < 75) {
    return LOADING_TALKING_POINTS_TEXT;
  }
  return 'Preparing data for custom models...';
};

export const isAccountConnected = (account) =>
  [PERFORMANCE_EXTRACTION_STATUS.connected, PERFORMANCE_EXTRACTION_STATUS.done].includes(
    account.status
  );

/**
 * Map BE PerformanceCenterTextVariationsResult record to FE consumed format
 * @param {Object} item - BE data
 * @returns {Object} - FE data
 */
export const mapBackendPerformanceTextVariation = (item) => {
  const parsedPerformance = parseJson(item.performance, {});
  return {
    ...item,
    weightedCtr: item.weightedAvgCtr,
    impressions: item.lifetimeImpressions,
    performance: parsedPerformance,
    ...parsedPerformance,
  };
};
